import { FAQ_ITEMS } from "../views/landing/landingContent";
import {
  FAQ_ITEMS as CLASSROOM_AUTOMATION_FAQ_ITEMS,
  PAGE_PATH as CLASSROOM_AUTOMATION_PATH,
  PAGE_SEO as CLASSROOM_AUTOMATION_SEO,
} from "../views/classroom-automation/classroomAutomationContent";
import blogs from "../blogs.js";
import {
  buildArticleSchema,
  buildBlogPostingSchema,
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildWebApplicationSchema,
} from "../utils/structuredData";
import { CLASSROOM_TOOL_PAGES } from "../views/classroom-tools/classroomToolPagesContent";

const CLASSROOM_TOOL_STRUCTURED_DATA = Object.fromEntries(
  Object.entries(CLASSROOM_TOOL_PAGES).map(([path, page]) => [
    path,
    {
      name: page.h1,
      description: page.metaDescription,
      faqs: page.faqs,
    },
  ])
);

const TOOL_PAGES = {
  "/classroom-timer": {
    name: "Free Classroom Timer",
    description:
      "Display a countdown timer on your classroom projector or smartboard. Set minutes and seconds, start, pause, and reset.",
    faqs: [
      {
        question: "Is this classroom timer free?",
        answer:
          "Yes. The timer runs in your browser with no account required. Create a Lesson Launchpad account to automate timers across your full daily schedule.",
      },
      {
        question: "Can I use this on a Smartboard or Promethean board?",
        answer:
          "Yes. Open the page in your board's browser, go fullscreen, and project the timer for the whole class.",
      },
      {
        question: "Does the timer play a sound when time is up?",
        answer:
          "Yes. An optional bell sound plays when the countdown reaches zero.",
      },
    ],
  },
  "/countdown-timer": {
    name: "Free Countdown Timer for Teachers",
    description:
      "Quick countdown timer for transitions, station rotations, tests, and brain breaks in any classroom.",
    faqs: [
      {
        question: "What preset times work best in class?",
        answer:
          "Teachers commonly use 2–5 minutes for transitions, 10–20 minutes for stations, and 30–60 seconds for quick think-pair-share activities.",
      },
      {
        question: "Can I automate countdowns for every period?",
        answer:
          "This free tool runs one timer at a time. Lesson Launchpad can start countdowns automatically based on your bell schedule.",
      },
    ],
  },
  "/random-student-picker": {
    name: "Random Student Picker",
    description:
      "Pick a random student name fairly during class discussions, cold calls, and participation checks.",
    faqs: [
      {
        question: "How do I add my class roster?",
        answer:
          "Paste student names one per line or comma-separated. The picker randomly selects without repeating until every name has been chosen.",
      },
      {
        question: "Is the selection truly random?",
        answer:
          "Yes. Names are shuffled using a fair random algorithm so every student has an equal chance each round.",
      },
    ],
  },
  "/music-tools": {
    name: "Free Music Tools for Teachers",
    description:
      "Browser-based metronome, tuner, and tone generator tools for music classrooms. No account required.",
    faqs: [
      {
        question: "Are these music tools free?",
        answer:
          "Yes. The metronome, tuner, and tone generator run in your browser with no account required.",
      },
      {
        question: "Can I embed these tools on my school website or LMS?",
        answer:
          "Yes. Use the embed URLs on the music tools page to add an iframe that shows only the tool.",
      },
      {
        question: "Do these work on a Smartboard or projector?",
        answer:
          "Yes. Open the page in your board's browser or embed the tool in your LMS.",
      },
    ],
  },
  "/metronome": {
    name: "Free Online Metronome",
    description:
      "Adjust tempo, subdivisions, and volume for classroom rehearsal, warm-ups, and steady practice.",
    faqs: [
      {
        question: "Is this metronome free?",
        answer:
          "Yes. It runs in your browser with no account required.",
      },
      {
        question: "What tempo range does it support?",
        answer:
          "You can set tempo from 40 to 220 BPM with quarter, eighth, triplet, or sixteenth subdivisions.",
      },
      {
        question: "Can I embed this metronome on my site?",
        answer:
          "Yes. Use the embed URL on the music tools page for an iframe that shows only the metronome widget.",
      },
    ],
  },
  "/tone-generator": {
    name: "Free Online Tone Generator",
    description:
      "Play reference pitches for tuning, ear training, and starting notes during music class.",
    faqs: [
      {
        question: "Is this tone generator free?",
        answer:
          "Yes. It runs in your browser with no login required.",
      },
      {
        question: "Can I choose equal-tempered or just intonation?",
        answer:
          "Yes. Switch between equal-tempered and just intonation to match your classroom tuning needs.",
      },
      {
        question: "Can I change A4 from 440 to 442 Hz?",
        answer:
          "Yes. Use the A4 control to set concert pitch from 430 to 450 Hz, including 442.",
      },
      {
        question: "Can I embed this on my LMS?",
        answer:
          "Yes. Use the embed URL on the music tools page for a tool-only iframe.",
      },
    ],
  },
  "/tuner": {
    name: "Free Online Tuner",
    description:
      "Check intonation in real time during rehearsal. Set A4 concert pitch, including 442 Hz.",
    faqs: [
      {
        question: "Is this tuner free?",
        answer:
          "Yes. It uses your device microphone and runs in the browser with no account.",
      },
      {
        question: "Does it work for strings, winds, and voice?",
        answer:
          "Yes. The tuner detects pitch in real time for common classroom instruments and voice.",
      },
      {
        question: "Can I change A4 from 440 to 442 Hz?",
        answer:
          "Yes. Use the A4 control to set concert pitch from 430 to 450 Hz, including 442.",
      },
      {
        question: "Can I embed the tuner without the surrounding page?",
        answer:
          "Yes. Use the embed URL on the music tools page for a tool-only iframe. Keep allow=\"microphone\" on the iframe so Canvas or another LMS can prompt for mic access.",
      },
    ],
  },
  ...CLASSROOM_TOOL_STRUCTURED_DATA,
};

const COMPARISON_PAGES = {
  "/classroomscreen-alternative": {
    title: "ClassroomScreen Alternative for Teachers | Automate Classroom Routines",
    description:
      "Looking for a ClassroomScreen alternative? Learn how Lesson Launchpad automates classroom timers, reminders, music, transitions, and daily routines so teachers can focus on teaching.",
    faqs: [
      {
        question: "What is the best ClassroomScreen alternative?",
        answer:
          "For teachers who want automation, not just widgets, Lesson Launchpad is a strong ClassroomScreen alternative. It automates timers, music, reminders, transitions, and daily routines from a single present screen tied to your schedule.",
      },
      {
        question: "What is the difference between ClassroomScreen and Lesson Launchpad?",
        answer:
          "ClassroomScreen focuses on manual on-screen widgets teachers activate during class. Lesson Launchpad focuses on Classroom Automation: timers, agendas, music, reminders, and class changes that run automatically based on your daily schedule.",
      },
      {
        question: "Can Lesson Launchpad automate classroom routines?",
        answer:
          "Yes. Lesson Launchpad lets teachers build automated routines for arrival, bell work, instruction, transitions, and dismissal. Events fire on schedule so routines stay consistent without manual setup each period.",
      },
      {
        question: "Can timers start automatically?",
        answer:
          "Yes. Lesson Launchpad supports Auto Timers that start and stop on your schedule during bell work, group work, cleanup, and transitions, without manually launching a timer widget each time.",
      },
      {
        question: "Can Lesson Launchpad automate classroom transitions?",
        answer:
          "Yes. Lesson Launchpad can play transition music, show countdowns, update agendas, and advance between classes automatically, reducing the manual steps teachers take during every transition.",
      },
      {
        question: "Does Lesson Launchpad include classroom reminders?",
        answer:
          "Yes. Teachers can schedule reminders for announcements, deadlines, field trips, and other tasks. Reminders display on the present screen at the scheduled time so you do not have to remember them mid-lesson.",
      },
      {
        question: "What is Classroom Automation?",
        answer:
          "Classroom Automation uses technology to manage recurring classroom routines, timers, reminders, schedules, and transitions automatically, so teachers spend less time operating tools and more time teaching. Learn more in our guide to Classroom Automation.",
      },
      {
        question: "Can Classroom Automation improve classroom management?",
        answer:
          "Yes. Predictable automated routines, visible agendas, and timed transitions help students know what to expect. That consistency supports smoother classroom management and reduces repeated directions.",
      },
      {
        question: "Does Lesson Launchpad work for elementary teachers?",
        answer:
          "Yes. Elementary teachers use Lesson Launchpad for arrival routines, transition music, timed activities, and consistent daily agendas that help younger students follow predictable classroom procedures.",
      },
      {
        question: "Does Lesson Launchpad work for middle school teachers?",
        answer:
          "Yes. Middle school teachers benefit from automated pacing timers, reminders, and schedule-driven routines across multiple classes and fast-moving daily transitions.",
      },
      {
        question: "Does Lesson Launchpad work for high school teachers?",
        answer:
          "Yes. High school teachers use automatic class changes, period-specific agendas, dismissal countdowns, and scheduled reminders to manage back-to-back classes and tight bell schedules.",
      },
      {
        question: "Can Lesson Launchpad replace multiple classroom tools?",
        answer:
          "For many teachers, yes. Instead of juggling separate timers, music apps, slide decks, and reminder systems, Lesson Launchpad combines display and automation in one present screen tied to the school day.",
      },
    ],
  },
  "/compare/classroomscreen-vs-lesson-launchpad": {
    title: "ClassroomScreen vs Lesson Launchpad",
    description:
      "Side-by-side comparison of ClassroomScreen and Lesson Launchpad for K-12 teachers.",
    faqs: [
      {
        question: "Which tool is better for daily classroom automation?",
        answer:
          "ClassroomScreen excels at manual on-screen widgets. Lesson Launchpad is built for teachers who want timers, agendas, and music to run automatically all day.",
      },
    ],
  },
  "/compare/google-slides-classroom-timer": {
    title: "Google Slides Classroom Timer Alternative",
    description:
      "Why teachers switch from Google Slides timers to a dedicated classroom automation platform.",
    faqs: [
      {
        question: "Can I replace a Google Slides timer with Lesson Launchpad?",
        answer:
          "Yes. Lesson Launchpad displays timers on your present screen without editing slides, and can start them automatically at scheduled times.",
      },
    ],
  },
  "/compare/smartboard-classroom-tools": {
    title: "Smartboard Classroom Tools",
    description:
      "Free and premium classroom tools that work on Smartboards, Promethean boards, and projectors.",
    faqs: [
      {
        question: "Do Lesson Launchpad tools work on interactive whiteboards?",
        answer:
          "Yes. All web-based tools and the full present screen run in the board browser or connected computer display.",
      },
    ],
  },
};

function toolSchemas(path, config) {
  return [
    buildWebApplicationSchema({
      name: config.name,
      description: config.description,
      url: path,
    }),
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Teacher Tools", url: "/tools" },
      { name: config.name, url: path },
    ]),
    buildFaqSchema(config.faqs),
  ].filter(Boolean);
}

function comparisonSchemas(path, config) {
  return [
    buildArticleSchema({
      title: config.title,
      description: config.description,
      url: path,
    }),
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: config.title, url: path },
    ]),
    buildFaqSchema(config.faqs),
  ].filter(Boolean);
}

function blogSchemas(pathname) {
  const blogId = pathname.match(/^\/blog\/([^/]+)$/)?.[1];
  if (!blogId) {
    return null;
  }

  const blogPost = blogs.find((blog) => blog.id === blogId || blog.url === blogId);
  if (!blogPost) {
    return null;
  }

  const path = `/blog/${blogPost.url || blogPost.id}`;

  return [
    buildBlogPostingSchema({
      title: blogPost.title,
      description: blogPost.description,
      url: path,
      datePublished: blogPost.date || undefined,
      image: blogPost.cardImage,
    }),
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Blog", url: "/blog" },
      { name: blogPost.title, url: path },
    ]),
  ];
}

const PAGE_SCHEMA_BUILDERS = {
  "/": () => [
    buildWebApplicationSchema({
      name: "Lesson Launchpad",
      description:
        "Classroom presentation and automation software for K-12 teachers. Display and automate timers, agendas, reminders, music, transitions, and class changes from one present screen.",
      url: "/",
    }),
    buildBreadcrumbSchema([{ name: "Home", url: "/" }]),
    buildFaqSchema(FAQ_ITEMS),
  ],
  "/pricing": () => [
    buildWebApplicationSchema({
      name: "Lesson Launchpad",
      description:
        "Classroom automation software for teachers. Automate timers, agendas, reminders, and daily routines.",
      url: "/pricing",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        description: "Free trial; paid plans available",
        url: "https://lessonlaunchpad.com/pricing",
      },
    }),
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Pricing", url: "/pricing" },
    ]),
  ],
  "/for-administrators": () => [
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "For Administrators", url: "/for-administrators" },
    ]),
  ],
  "/tools": () => [
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Teacher Tools", url: "/tools" },
    ]),
  ],
  [CLASSROOM_AUTOMATION_PATH]: () => [
    buildArticleSchema({
      title: CLASSROOM_AUTOMATION_SEO.title,
      description: CLASSROOM_AUTOMATION_SEO.description,
      url: CLASSROOM_AUTOMATION_PATH,
    }),
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "What Is Classroom Automation?", url: CLASSROOM_AUTOMATION_PATH },
    ]),
    buildFaqSchema(CLASSROOM_AUTOMATION_FAQ_ITEMS),
  ],
};

export function getStructuredDataForPath(pathname) {
  if (PAGE_SCHEMA_BUILDERS[pathname]) {
    return PAGE_SCHEMA_BUILDERS[pathname]();
  }

  if (TOOL_PAGES[pathname]) {
    return toolSchemas(pathname, TOOL_PAGES[pathname]);
  }

  if (COMPARISON_PAGES[pathname]) {
    return comparisonSchemas(pathname, COMPARISON_PAGES[pathname]);
  }

  if (pathname.startsWith("/blog/")) {
    return blogSchemas(pathname);
  }

  return null;
}

export { TOOL_PAGES, COMPARISON_PAGES };
